import React, { useState, useEffect } from "react";
import {
    Input,
    Button,
    Dropdown,
    Option,
    Table,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableBody,
    TableCell,
    TableCellLayout,
    Field,
    Spinner,
} from "@fluentui/react-components";
import {
    Add20Regular,
    Add20Filled,
    Edit20Filled,
    Dismiss20Regular,
    Delete20Regular,
    Save20Regular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import "../CSS/EntitieComponent.css";
import SearchResourceInGraph from "../GenericComponent/SearchResourceInGraph";
import {
    getScopeById,
    updateScope,
    addScope,
    getAllPermissions,
} from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useNavigate } from "react-router-dom";
import { useNotification } from '../GenericComponent/NotificationSystem';
import { useAbility } from "../../Contexts/AbilityContext";
import { ReadOnlyFieldset } from "../GenericComponent/ReadOnlyFieldset";

export default function ScopeForm({
    ACTIVE_TENANT,
    ALL_ROLES,
    setScopes,
    setScopesNames,
    setRoles,
    setAllRoles,
}) {
    const { t } = useTranslation();
    const { entityName } = useParams();
    const { instance, accounts } = useMsal();
    const activeAccount = accounts[0];
    const navigate = useNavigate();
    const isUpdate = entityName && entityName !== "NewEntity";

    const [scopeName, setScopeName] = useState("");
    const [admins, setAdmins] = useState([]);
    const [rolesList, setRolesList] = useState([]);
    const [showUserSearch, setShowUserSearch] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [saving, setSaving] = useState(false);

    // Cet écran ne consultait AUCUNE permission : il s'ouvrait toujours modifiable, quel que soit
    // le droit de l'utilisateur, et l'échec n'apparaissait qu'au retour du serveur. Même sujet
    // CASL que la liste — `Entity.main@<nom de l'entité>`.
    //
    // Refus par DÉFAUT (`!== true`) : tant qu'`ability` n'est pas prêt, on ne présume pas du droit.
    const ability = useAbility();
    const readOnly = isUpdate
        && !(ability != null && ability.can("update", `Entity.main@${entityName}`));
    const { showSuccess, showError } = useNotification();

    // --- Déterminer le rôle du user courant ---
    const getCurrentUserId = () => activeAccount?.localAccountId || activeAccount?.homeAccountId;
    const getCurrentUserRole = () => {
        const userId = getCurrentUserId();
        const user = admins.find(a => a.id === userId);
        return user ? (user.roleName || '').toLowerCase() : null;
    };

    // --- Load roles ---
    useEffect(() => {
        if (!ALL_ROLES) return;
        const list = Object.values(ALL_ROLES).map((r) => ({
            id: r.id,
            name: r.displayName || "Default",
        }));
        setRolesList(list);
        console.log("Loaded roles:", list);
    }, [ALL_ROLES]);

    // --- Load scope data for update ---
    useEffect(() => {
        if (!isUpdate) {
            // For new scope, initialize with empty name
            if (entityName === "NewEntity") {
                setScopeName("");
                // Add current user as admin by default
                if (activeAccount && rolesList.length > 0) {
                    // Find admin role, fallback to first role if not found
                    const adminRole = rolesList.find((r) => r.name.toLowerCase() === "owner") || rolesList[0];
                    const currentUser = {
                        id: activeAccount.localAccountId || activeAccount.homeAccountId,
                        displayName: activeAccount.name || activeAccount.username,
                        roleId: adminRole.id,
                        roleName: adminRole.name,
                    };
                    setAdmins([currentUser]);
                }
            }
            return;
        }

        if (rolesList.length === 0) return;

        const loadScope = () => {
            setIsLoading(true);
            
            getAccessToken(instance, activeAccount)
                .then((token) => {
                    return getScopeById(ACTIVE_TENANT, entityName, token);
                })
                .then((data) => {
                    const scopeData = data?.scope || data;

                    setScopeName(scopeData?.id || scopeData?.RowKey || entityName || "");

                    // Dédoublonne adminsIds au chargement (robustesse contre d'anciens
                    // doublons en base) → évite d'afficher/réécrire plusieurs fois le même admin.
                    const uniqueAdminIds = [...new Set(scopeData?.admins?.adminsIds || [])];
                    const adminArray = uniqueAdminIds.map((id) => {
                        const roleIdFromScope = scopeData?.UsersRoles?.[id]?.RolesId;
                        const matchedRole = rolesList.find((r) => r.id === roleIdFromScope);

                        if (!matchedRole) return null;

                        return {
                            id,
                            displayName: scopeData?.admins?.adminsDisplayNames?.[id] || "Unknown User",
                            roleId: matchedRole.id,
                            roleName: matchedRole.name,
                        };
                    }).filter(Boolean);

                    setAdmins(adminArray);
                })
                .catch((err) => {
                    console.error("Error loading scope:", err);
                    showError(t("FAILED_TO_LOAD_SCOPE"), err?.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        };

        loadScope();
    }, [entityName, isUpdate, ACTIVE_TENANT, instance, activeAccount, rolesList]);

    // --- Admin actions ---
    const handleAddAdmin = (user) => {
        if (!user || !user.id) {
            showError(t("INVALID_USER_SELECTED"));
            return;
        }
        
        // Check if admin already exists
        const existingAdmin = admins.find((a) => a.id === user.id);
        if (existingAdmin) {
            showError(
                t("ADMIN_ALREADY_EXISTS"),
                `${existingAdmin.displayName} ${t("IS_ALREADY_IN_THE_LIST")}`
            );
            return;
        }

        // Déterminer le rôle assignable selon le rôle courant
        const currentUserRole = getCurrentUserRole();
        let allowedRole = null;
        if (currentUserRole === 'supervisor') {
            // Supervisor ne peut ajouter que supervisor
            allowedRole = rolesList.find(r => (r.name || '').toLowerCase() === 'supervisor');
        } else if (currentUserRole === 'admin') {
            // Admin peut ajouter supervisor ou admin (jamais owner)
            allowedRole = rolesList.find(r => (r.name || '').toLowerCase() === 'admin')
                || rolesList.find(r => (r.name || '').toLowerCase() === 'supervisor');
        } else {
            // Owner peut tout
            allowedRole = rolesList[0] || { id: "default", name: "Default" };
        }
        if (!allowedRole) {
            showError(t("INVALID_USER_SELECTED"), t("NO_ROLE_AVAILABLE"));
            return;
        }
        const newAdmin = {
            id: user.id,
            displayName: user.displayName || user.userPrincipalName,
            roleId: allowedRole.id,
            roleName: allowedRole.name,
        };
        setAdmins((prev) => [...prev, newAdmin]);
        showSuccess(t("ADMIN_ADDED_SUCCESSFULLY"), user.displayName || user.userPrincipalName);
    };

    const handleRemoveAdmin = (id) => {
        setAdmins((prev) => prev.filter((a) => a.id !== id));
    };
console.log("Admins list before role change:", rolesList);
    const handleRoleChange = (id, newRoleId) => {
        const newRole = rolesList.find((r) => r.id === newRoleId);
        if (newRole) {
            setAdmins((prevAdmins) =>
                prevAdmins.map((a) =>
                    a.id === id
                        ? {
                            ...a,
                            roleId: newRole.id,
                            roleName: newRole.name,
                        }
                        : a
                )
            );
        }
    };

    // --- Validate scope name ---
    const validateScopeName = (name) => {
        if (!name.trim()) {
            return t("SCOPE_NAME_REQUIRED");
        }
        if (name.length < 3) {
            return t("SCOPE_NAME_MIN_LENGTH");
        }
        // Add more validation rules if needed
        return "";
    };

    // --- Save or update ---
    const handleSubmit = () => {
        const error = validateScopeName(scopeName);
        if (error) {
            setValidationError(error);
            return;
        }
        setValidationError("");

        // Create admins data — dédoublonne par id (robustesse, jamais 2x le même admin)
        const adminsIds = [...new Set(admins.map((a) => a.id))];
        const adminsDisplayNames = admins.reduce(
            (acc, a) => ({ ...acc, [a.id]: a.displayName }),
            {}
        );
        
        const UsersRoles = admins.length > 0 ? admins.reduce(
            (acc, a) => ({
                ...acc,
                [a.id]: { 
                    displayName: a.displayName, 
                    RolesId: a.roleId 
                },
            }),
            {}
        ) : {};

        const scopeData = {
            id: scopeName.trim(),
            RowKey: scopeName.trim(),
            admins: { 
                adminsIds, 
                adminsDisplayNames 
            },
            UsersRoles,
            Timestamp: new Date().toISOString(),
        };

        setSaving(true);
        
        return getAccessToken(instance, activeAccount)
            .then((token) => {
                if (isUpdate) {
                    return updateScope(ACTIVE_TENANT, entityName, token, scopeData);
                } else {
                    return addScope(ACTIVE_TENANT, token, scopeData);
                }
            })
            .then((response) => {
                // Check if the operation failed
                if (response?.data?.success === false) {
                    const errorMsg = response.data.message || "Operation failed";
                    showError(errorMsg);
                    setSaving(false);
                    return null; // Stop the chain without throwing
                }
                
                // Refresh scopes in Redux store
                return getAccessToken(instance, activeAccount);
            })
            .then((token) => {
                if (!token) return null; // Skip if previous step failed
                return getAllPermissions(ACTIVE_TENANT, token);
            })
            .then((data) => {
                if (!data) return; // Skip if previous step failed
                
                const { scopes, roles } = data;
                const scopeNames = Object.keys(scopes);
                
                // Update Redux store - cela va automatiquement mettre à jour ListEntitiesComponent
                setScopes(scopes);
                setScopesNames(scopeNames);
                
                showSuccess(isUpdate ? t("SCOPE_UPDATED_SUCCESS") : t("SCOPE_CREATED_SUCCESS"));
                
                // Navigate back
                navigate("/entities");
            })
            .catch((err) => {
                console.error("Error saving scope:", err);
                
                // Parser l'erreur si elle est stringifiée
                let errorData;
                try {
                    errorData = JSON.parse(err.message);
                } catch {
                    errorData = { message: err.message, status: 500 };
                }
                
                // Vérifier si c'est une erreur 409 (scope déjà existant)
                if (errorData.status === 409 || errorData.message === "Scope already exists") {
                    showError(t("SCOPE_ALREADY_EXISTS"));
                } else {
                    showError(
                        isUpdate ? t("FAILED_TO_UPDATE_SCOPE") : t("FAILED_TO_CREATE_SCOPE"),
                        errorData.message
                    );
                }
            })
            .finally(() => {
                setSaving(false);
            });
    };

    return (
        <div className="entity-form-root">
            {/* Header buttons */}
            <div className="entity-form-header-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <Button 
                 icon={<Dismiss20Regular />}
                    appearance="secondary"
                    onClick={() => navigate("/entities")}
                    disabled={isLoading}
                >
                    {t("CANCEL")}
                </Button>
                {/* Masqué en lecture seule, pas grisé : grisé, il laisse croire qu'il manque
                    une saisie, alors qu'il s'agit d'un droit absent. */}
                {!readOnly && (
                  <Button 
                    appearance="primary"
                    icon={saving ? <Spinner size="tiny" /> : <Save20Regular />}
                    onClick={handleSubmit}
                    disabled={saving || isLoading}
                  >
                    { t("SAVE")}
                  </Button>
                )}
            </div>

            {/* Le fieldset n'enveloppe que le CORPS : les boutons d'en-tête (Annuler) doivent
                rester cliquables, et un fieldset désactivé désactiverait aussi ceux-là. */}
            <ReadOnlyFieldset readOnly={readOnly}>
            {/* Title    <h2 className="entity-form-title">
                {isUpdate ? `${t("EDIT_SCOPE")}: ${entityName}` : t("ADD_NEW_SCOPE")}
            </h2>*/}
         

            {/* Scope name */}
            <Field
            
                label={t("SCOPE_NAME")}
                className="entity-form-field-wrapper"
                validationMessage={validationError}
                validationState={validationError ? "error" : "none"}
            >
                <Input
                    placeholder={t("ENTER_SCOPE_NAME")}
                    value={scopeName}
                    className="entitie-form-input"
                    onChange={(e, data) => {
                        const valueWithoutSpaces = data.value.replace(/\s/g, '');
                        setScopeName(valueWithoutSpaces);
                        if (validationError) setValidationError("");
                    }}
                    disabled={isUpdate || isLoading}
                    required
                />
            </Field>

            {/* Admins Table */}
            <div className="entity-form-table-container">
                  <Button
                        appearance="primary"
                        icon={<Add20Regular />}
                        onClick={() => setShowUserSearch(true)}
                        className="entity-form-add-admin-button"
                        disabled={isLoading || !scopeName.trim()}
                    >
                    </Button>
              

                {showUserSearch && (
                    <div className="entity-form-search-container">
                        <SearchResourceInGraph
                            onSelectUser={(user) => {
                                handleAddAdmin(user);
                                setShowUserSearch(false);
                            }}
                            onCancel={() => setShowUserSearch(false)}
                            disabled={isLoading}
                        />
                    </div>
                )}

                {/* Table */}
                <div className="entity-form-table-wrapper">
                    <Table role="grid" className="entity-form-table-style">
                        <TableHeader>
                            <TableRow className="entity-form-header-style">
                                <TableHeaderCell>{t("NAME")}</TableHeaderCell>
                                <TableHeaderCell>{t("ROLE")}</TableHeaderCell>
                                <TableHeaderCell>{t("ACTION")}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {admins.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <TableCellLayout className="entity-form-empty-state">
                                            {t("NO_ADMINS_ADDED")}
                                        </TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                admins.map((admin) => {
                                    const currentUserRole = getCurrentUserRole();
                                    const adminRoleName = (admin.roleName || '').toLowerCase();
                                    // Supervisor ne peut pas éditer admin/owner
                                    const isSupervisorBlocked = currentUserRole === 'supervisor' && (adminRoleName === 'admin' || adminRoleName === 'owner');
                                    // Admin ne peut pas éditer owner
                                    const isAdminBlocked = currentUserRole === 'admin' && adminRoleName === 'owner';
                                    const dropdownDisabled = isLoading || isSupervisorBlocked || isAdminBlocked;
                                    const deleteDisabled = isLoading || isSupervisorBlocked || isAdminBlocked;
                                    return (
                                        <TableRow key={admin.id}>
                                            <TableCell>
                                                <TableCellLayout>{admin.displayName}</TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <Dropdown
                                                    value={t(admin.roleName.toUpperCase().replace(/-/g, '_'))}
                                                    onOptionSelect={(_, data) =>
                                                        handleRoleChange(admin.id, data.optionValue)
                                                    }
                                                    className="entity-form-role-dropdown"
                                                    disabled={dropdownDisabled}
                                                >
                                                    {rolesList.map((role) => {
                                                        // Règles de restriction selon le rôle courant
                                                        const roleName = (role.name || '').toLowerCase();
                                                        let optionDisabled = false;
                                                        if (currentUserRole === 'supervisor') {
                                                            if (roleName === 'admin' || roleName === 'owner') optionDisabled = true;
                                                        } else if (currentUserRole === 'admin') {
                                                            if (roleName === 'owner') optionDisabled = true;
                                                        }
                                                        return (
                                                            <Option key={role.id} value={role.id} disabled={optionDisabled}>
                                                                {t(role.name.toUpperCase().replace(/-/g, '_'))}
                                                            </Option>
                                                        );
                                                    })}
                                                </Dropdown>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    icon={<Delete20Regular />}
                                                    appearance="subtle"
                                                    onClick={() => handleRemoveAdmin(admin.id)}
                                                    disabled={deleteDisabled}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
            </ReadOnlyFieldset>
        </div>
    );
}
